import { useState } from 'react';
import { otkrytNomer, type DverSushchnost } from './dver';
import { useOptionalPhone } from '../telephony/usePhone';
import { usePermissions } from '../auth/usePermissions';
import { naborIzGotovogo } from '../telephony/nomerNabora';

/**
 * Маска телефона — СКВОЗНОЙ компонент (`zhur1`, владелец 08.09.2026;
 * паттерн раскрытия уточнён владельцем 08.09 вечером: отдельной кнопки
 * «показать» НЕТ — маскированный номер рисуется с пунктирным подчёркиванием,
 * и КЛИК ПО САМОМУ НОМЕРУ раскрывает полный).
 *
 * На экран приходит номер КРАЯМИ (сервер полного не отдаёт вовсе):
 *   клик по номеру — ход reveal `phone.reveal`: сервер сначала ЗАПИСЫВАЕТ
 *                    раскрытие в журнал ходьбы и только потом отдаёт номер;
 *   «позвонить»    — ход click `phone.call`: номер уходит прямо в трубку
 *                    оператора, НЕ появляясь на экране.
 *
 * ⛔ Заводить маску поэкранно запрещено: следующий построенный экран показал бы
 * номер целиком, и этого никто бы не заметил. Все экраны с номером — включая
 * телефонию (журнал звонков, пульт, окно разговора, карточка звонка — решение
 * владельца 08.09) — через этот компонент.
 */
export interface MaskaTelefonaProps {
  /** Маска краями с сервера; null/пусто — номера нет. */
  masked: string | null | undefined;
  /** Сущность-хозяин номера — как в журнале (имя таблицы + id). */
  entityType: DverSushchnost;
  entityId: number;
  /** Без кнопки «позвонить» (например, там, где звонить не с чего). */
  bezZvonka?: boolean;
  /** Текст, когда номера нет (по умолчанию «номера нет»). */
  pusto?: string;
  className?: string;
  /**
   * Номер раскрыт — хозяину экрана, чтобы он показал СВОИ действия по номеру.
   *
   * ⛔ ЭТО НЕ «ВЕРНУТЬ НОМЕР НАРУЖУ», А СИГНАЛ «ЧЕЛОВЕК ПОСМОТРЕЛ». Экран с
   * собственной кнопкой звонка (мини-карточка звонка) обязан прятать её до
   * раскрытия по тому же правилу, что и кнопка внутри маски; заведи ему свой
   * флаг «раскрыт» — и правило разъедется на два, а одно из них завтра отстанет.
   */
  onRaskryt?: () => void;
}

export function MaskaTelefona({
  masked,
  entityType,
  entityId,
  bezZvonka,
  pusto = 'номера нет',
  className,
  onRaskryt,
}: MaskaTelefonaProps) {
  const [otkrytyy, setOtkrytyy] = useState<string | null>(null);
  const [zanyat, setZanyat] = useState(false);
  const telefon = useOptionalPhone();
  const { rezhimNomera } = usePermissions();
  const mozhnoRaskryt = rezhimNomera !== 'naglukho';

  const raskryt = async () => {
    if (zanyat || otkrytyy) return;
    setZanyat(true);
    try {
      setOtkrytyy(await otkrytNomer(entityType, entityId, 'show'));
      onRaskryt?.();
    } catch {
      // Дверь не открылась — маска остаётся, панель живёт.
    } finally {
      setZanyat(false);
    }
  };

  const pozvonit = async () => {
    if (zanyat) return;
    setZanyat(true);
    try {
      const nomer = await otkrytNomer(entityType, entityId, 'call');
      // Номер уходит в трубку, на экране НЕ появляется.
      // `s713` Д-13: в трубку — номер по общему правилу набора; непохожее на номер отклонит сама дверь звонка.
      if (nomer && telefon) telefon.dial(naborIzGotovogo(nomer) ?? nomer.replace(/\D+/g, ''));
    } catch {
      // Не дозвонились до двери — молча, без падения экрана.
    } finally {
      setZanyat(false);
    }
  };

  if (!masked) {
    return <span className={className}>{pusto}</span>;
  }

  /*
   * ⛔ «ОТКРЫТ ВСЕГДА» — НОМЕР УЖЕ ПРИШЁЛ ЦЕЛИКОМ (`s318`, владелец 11.09.2026).
   *
   * Края необратимы, поэтому положение решается на сервере, в той самой
   * функции, которая превращает номер в маску: при этом режиме она отдаёт
   * полный. Экрану остаётся не звать нажимать на то, что уже открыто, — но
   * «позвонить» остаётся, и звонок по-прежнему идёт через дверь со следом.
   */
  if (rezhimNomera === 'otkryt') {
    return (
      <span className={className} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
        <span>{masked}</span>
        {!bezZvonka && telefon !== null && (
          <button
            type="button"
            className="btn small"
            data-act="phone.mask.call"
            data-act-entity={entityType}
            data-act-id={entityId}
            disabled={zanyat}
            onClick={() => void pozvonit()}
            title="Позвонить"
          >
            Позвонить
          </button>
        )}
      </span>
    );
  }

  /*
   * ⛔ «НАГЛУХО» — РАСКРЫТЬ НЕЛЬЗЯ НИЧЕМ (`s318`; повод — `s521`, 11.09.2026).
   *
   * Жалоба владельца: директолог кликнул по скрытому номеру — и увидел его
   * целиком. Замок стоит на двери (`POST browse/phone`, проверка режима), и он
   * настоящий: экран его только повторяет, чтобы не звать человека нажимать на
   * то, что всё равно откажет. Пунктира нет, курсора нет, кнопки «Позвонить»
   * нет — маска остаётся обычным текстом.
   *
   * ⛔ Ни имени права, ни имени режима наружу (op-rules §31.5): человек видит
   * скрытый номер и ничего больше.
   */
  if (!mozhnoRaskryt) {
    return <span className={className}>{masked}</span>;
  }

  return (
    <span className={className} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      {otkrytyy !== null ? (
        <span>{otkrytyy}</span>
      ) : (
        /* Клик по самому номеру — и есть «показать» (владелец 08.09):
           пунктир зовёт нажать, отдельной кнопки нет. */
        <span
          role="button"
          tabIndex={0}
          data-act="phone.mask.show"
          data-act-entity={entityType}
          data-act-id={entityId}
          onClick={() => void raskryt()}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              void raskryt();
            }
          }}
          title="Показать номер целиком — раскрытие записывается"
          style={{
            textDecoration: 'underline dashed',
            textUnderlineOffset: 3,
            cursor: zanyat ? 'progress' : 'pointer',
          }}
        >
          {masked}
        </span>
      )}
      {/* ⛔ «ПОЗВОНИТЬ» ПОЯВЛЯЕТСЯ ТОЛЬКО ПОСЛЕ РАСКРЫТИЯ НОМЕРА (владелец,
          09.09.2026). Кнопка стояла рядом с маской всегда — и звонок уходил
          мимо раскрытия: в журнале ходьбы оставался ход `phone.call` без
          предшествующего `phone.reveal`, то есть человек звонил, ни разу не
          посмотрев, кому. Раскрытие — осознанный шаг, и звонок идёт после него.
          Условие живёт ЗДЕСЬ, в одном сквозном компоненте: разложи его по
          экранам — и следующий построенный экран показал бы кнопку сразу. */}
      {!bezZvonka && telefon !== null && otkrytyy !== null && (
        <button
          type="button"
          className="btn small"
          data-act="phone.mask.call"
          data-act-entity={entityType}
          data-act-id={entityId}
          disabled={zanyat}
          onClick={() => void pozvonit()}
          title="Позвонить — номер уйдёт в трубку, не раскрываясь"
        >
          Позвонить
        </button>
      )}
    </span>
  );
}
